import React, { Component } from "react";
import { connect } from "react-redux";
import { ThreeDots } from "react-bootstrap-icons";
import moment from "moment";
import Promise from "bluebird";

import styles from "./Agenda.styles";

import * as actions from "../../../redux/collaborators/actions";
import {
  loadCollaborators,
  isFetchingCollaborators,
  loadCollaboratorInvites,
  getCollaborators,
  getInvites,
  inviteCollaborator,
  createCollaborator,
  loadTimelineEvents,
  getTimelineEvents,
} from "../../../redux/collaborators";
import { getActiveProject, getProjects } from "../../../redux/projects";
import { getUser, isLoggedIn } from "../../../redux/user";

import type User from "../../models/User";
import type Project from "../../models/Project";
import type ProjectCollection from "../../models/ProjectCollection";
import type TimelineEventColection from "../../models/TimelineEvent";
import type TimelineEvent from "../../models/TimelineEvent";
import { Link } from "react-router-dom";
import type { CreateProjectParamsObject } from "../../../redux/projects/types";

import ProjectModal from "../components/modals/project-modal/ProjectModal";
import ProjectModalDelete from "../components/modals/project-modal-delete/ProjectModalDelete";
import ProjectModalLeave from "../components/modals/project-modal-leave/ProjectModalLeave";
import ProjectImage from "../components/project-image/ProjectImage";
import Collaborators from "./collaborators/Collaborators";
import OptsDropdown from "../projects/OptsDropdown";
import { accountExpired, canAddProject } from "../../../helpers/Utils";

type OwnProps = {
  user: User,
  projects: ProjectCollection,
  createTimelineEvent: (params: CreateTimelineEventObject) => Promise<void>,
  onProjectDelete: () => Promise<void>,
  onProjectLeave: () => Promise<void>,
  onRequestShowAgenda: () => void,
  onRequestShowTaskList: () => void,
  onRequestShowTimeline: () => void,
};

type StateProps = {
  activeTab: TabKey,
  activeProject: ?Project,
  isLoggedIn: boolean,
  isAddingUser: boolean,
  isFetchingCollaborators: boolean,
  collaborators: CollaboratorCollection,
  invites: CollaboratorInviteCollection,
  tempUser: ?User,
  timelineEvents: TimelineEventCollection,
};

type Props = OwnProps & StateProps;

type State = {
  showUpdateProjectModal: boolean,
  showDeleteProjectModal: boolean,
  showDuplicateProjectModal: boolean,
  showLeaveProjectModal: boolean,
};

class Agenda extends Component {
  props: Props;
  state: State = {
    showUpdateProjectModal: false,
    showDeleteProjectModal: false,
    showDuplicateProjectModal: false,
    showLeaveProjectModal: false,
  };
  loadCollaboratorsPromise: ?Promise<void>;
  loadCollaboratorInvitesPromise: ?Promise<void>;
  inviteCollaboratorPromise: ?Promise<void>;
  createCollaboratorPromise: ?Promise<void>;
  createEventPromise: ?Promise<void>;

  constructor(props: Props) {
    super(props);
  }

  componentDidMount() {
    const project = this.props.activeProject;
    // Added !collaborators.length so it doesn't refetch collaborators. It was
    // causing an issue where all coll's would disappear when clicking back to
    // the agenda page.
    if (project && !this.props.collaborators.length) {
      this.loadCollaboratorsPromise = Promise.join(
        this.props.loadCollaborators(project.id),
        this.props.loadCollaboratorInvites(project.id)
      );
      this.loadTimelineEventsPromise = Promise.resolve(
        this.props.loadTimelineEvents()
      );
    }
  }

  handleDuplicateProject = async (params) => {
    if (canAddProject(this.props.user, this.props.projects)) {
      const sourceEvents = this.props.timelineEvents;

      // duplicating project
      const sourceProject = this.props.activeProject;
      const projectId = await this.props.createProject({
        name: sourceProject.name + " copy",
        date: params.date || sourceProject.date,
        photoURI: sourceProject.photoURI || "",
        collaboratorEmail: sourceProject.collaboratorEmail,
        timezone: sourceProject.timezone,
      });

      // duplicating events
      if (projectId) {
        sourceEvents.data.map((event) => {
          // Calculate the date and pass it in in the format "2019-07-03T02:00:00-06:00"
          // for consistency with what the datepicker returns.
          const oldDateTime = event.timezonelessDateTime();
          let newDate = moment
            .utc(params.date, "MM/DD/YYYY")
            .format("YYYY-MM-DD");
          let oldTime = moment.utc(oldDateTime).format("HH:mm:ss");
          let newDateTime = moment.utc(`${newDate}T${oldTime}Z`).format();

          this.props.createTimelineEvent({
            name: event.name,
            dateTime: newDateTime || event.timezonlessDateTime(),
            location: event.location,
            details: event.details,
            projectId: projectId,
          });
        });
      }

      // reload afterward
      window.location.reload();
    }
  };

  onProjectDelete = () => {
    this.closeDeleteProjectModal();
    window.location.href = "/projects";
  };

  onProjectLeave = () => {
    this.closeLeaveProjectModal();
    window.location.href = "/projects";
  };

  openUpdateProjectModal = () => {
    this.setState({
      showUpdateProjectModal: true,
    });
  };

  closeUpdateProjectModal = () => {
    this.setState({
      showUpdateProjectModal: false,
    });
  };

  openDeleteProjectModal = () => {
    this.setState({
      showDeleteProjectModal: true,
    });
  };

  openLeaveProjectModal = () => {
    this.setState({
      showLeaveProjectModal: true,
    });
  };

  closeDeleteProjectModal = () => {
    this.setState({
      showDeleteProjectModal: false,
    });
  };

  openDuplicateProjectModal = () => {
    this.setState({
      showDuplicateProjectModal: true,
    });
  };

  closeDuplicateProjectModal = () => {
    this.setState({
      showDuplicateProjectModal: false,
    });
  };

  closeLeaveProjectModal = () => {
    this.setState({
      showLeaveProjectModal: false,
    });
  };

  render() {
    return (
      <div>
        {this.props.isFetchingCollaborators ? "" : this.renderAgenda()}
        {this.state.showUpdateProjectModal
          ? this.renderUpdateProjectModal()
          : ""}
        {this.state.showDeleteProjectModal
          ? this.renderDeleteProjectModal()
          : ""}
        {this.state.showDuplicateProjectModal
          ? this.renderDuplicateProjectModal()
          : ""}
        {this.state.showLeaveProjectModal ? this.renderLeaveProjectModal() : ""}
      </div>
    );
  }

  renderAgenda() {
    if (!this.props.user) {
      return null;
    }

    const showAgenda = !!this.props.activeProject;
    const project = this.props.activeProject;
    const userHasOwnerPrivileges =
      this.props.user &&
      this.props.activeProject &&
      this.props.activeProject.role === "owner";

    if (!showAgenda) {
      return (
        <div>
          <p>No active project.</p>
        </div>
      );
    }

    const imageHeight = 200;
    const imageWidth = 300;

    return (
      <div>
        <div className="agenda-title-container">
          <div className="project-title-container">
            <h1 className="project-title">{project.name}</h1>
            <h4 className="project-date">Date: {project.date}</h4>
            <OptsDropdown
              direction="right"
              project={project}
              facade={<ThreeDots size="20" />}
              handleEditClick={this.openUpdateProjectModal}
              handleDeleteClick={this.openDeleteProjectModal}
              handleDuplicateClick={this.openDuplicateProjectModal}
              accountExpired={accountExpired(this.props.user)}
              isOwner={userHasOwnerPrivileges}
            />
          </div>
          <ProjectImage
            url={project.photoURL}
            width={imageWidth}
            height={imageHeight}
          />
        </div>
        {this.props.renderProjectNav()}
        <Collaborators
          project={this.props.activeProject}
          user={this.props.user}
          collaborators={this.props.collaborators}
          invites={this.props.invites}
          handleAddCollaborator={this.handleAddCollaborator}
          onPress={this.handleAddUser}
          showCollaboratorInvite={userHasOwnerPrivileges}
          showInvite={this.state.showInvite}
          isOwner={userHasOwnerPrivileges}
        />
      </div>
    );
  }

  renderProjectEditButtons = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
        }}
      >
        <button
          className="btn btn-default"
          style={styles.button}
          onClick={() => this.openDeleteProjectModal()}
        >
          <i className="fa fa-trash"></i>
        </button>
        <button
          className="btn btn-default"
          style={styles.button}
          onClick={() => this.openUpdateProjectModal()}
        >
          <i className="fa fa-pencil"></i>
        </button>
        <button
          className="btn btn-default"
          style={styles.button}
          onClick={() => this.openDuplicateProjectModal()}
        >
          <i className="fa fa-copy"></i>
        </button>
      </div>
    );
  };

  renderProjectLeaveButton = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
        }}
      >
        <button
          className="btn btn-default"
          style={styles.button}
          onClick={() => this.openLeaveProjectModal()}
        >
          Leave Project
        </button>
      </div>
    );
  };

  renderUpdateProjectModal = () => {
    return (
      <ProjectModal
        onModalClose={() => this.closeUpdateProjectModal()}
        user={this.props.user}
        project={this.props.activeProject}
      ></ProjectModal>
    );
  };

  renderDeleteProjectModal = () => {
    return (
      <ProjectModalDelete
        onModalClose={() => this.closeDeleteProjectModal()}
        onDelete={() => this.onProjectDelete()}
        user={this.props.user}
        project={this.props.activeProject}
      ></ProjectModalDelete>
    );
  };

  renderDuplicateProjectModal = () => {
    return (
      <ProjectModal
        duplicate={true}
        handleDuplicateProject={this.handleDuplicateProject}
        user={this.props.user}
        project={this.props.activeProject}
        onModalClose={() => this.closeDuplicateProjectModal()}
      ></ProjectModal>
    );
  };

  renderLeaveProjectModal = () => {
    return (
      <ProjectModalLeave
        onModalClose={() => this.closeLeaveProjectModal()}
        onLeave={() => this.onProjectLeave()}
        user={this.props.user}
        project={this.props.activeProject}
      ></ProjectModalLeave>
    );
  };
}

const isNarrowScreen = () => {
  if (window.innerWidth > 600) return false;
  return true;
};

const mapStateToProps = (state) => {
  return {
    user: getUser(state),
    activeProject: getActiveProject(state),
    isLoggedIn: isLoggedIn(state),
    isFetchingCollaborators: isFetchingCollaborators(state),
    collaborators: getCollaborators(state),
    invites: getInvites(state),
    projects: getProjects(state),
    timelineEvents: getTimelineEvents(state),
  };
};

export default connect(mapStateToProps, actions)(Agenda);



// WEBPACK FOOTER //
// ./src/views/user/agenda/Agenda.js